import { useState, useRef, useCallback, useEffect } from 'react';
import { API_URL } from '../config';
import api from '../api/client';

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];

/**
 * useSelleriseUpload
 *
 * Управляет полным флоу для двух режимов:
 *   'weekly'  — выбор диапазона дат,           загрузка в amazon_week_sales
 *   'monthly' — выбор нескольких месяцев/года,  загрузка в amazon_month_sales
 */
export function useSelleriseUpload(onSuccess) {
  // ── Режим ─────────────────────────────────────────────────────────────
  const [mode, setMode] = useState('weekly');

  // ── Weekly state ──────────────────────────────────────────────────────
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');

  // ── Monthly state ─────────────────────────────────────────────────────
  const [selectedYear, setSelectedYear]     = useState(new Date().getFullYear());
  const [selectedMonths, setSelectedMonths] = useState([]); // массив чисел 1–12

  // ── Shared state ──────────────────────────────────────────────────────
  const [showPopup, setShowPopup]       = useState(false);
  const [scrapedRows, setScrapedRows]   = useState([]);
  const [scrapeStatus, setScrapeStatus] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);

  const eventSourceRef = useRef(null);

  useEffect(() => {
    return () => { if (eventSourceRef.current) eventSourceRef.current.close(); };
  }, []);

  // ── Open / Close ───────────────────────────────────────────────────────
  const openPopup = useCallback(() => {
    setShowPopup(true);
    setScrapedRows([]);
    setScrapeStatus(null);
    setUploadStatus(null);
    setUploadErrors([]);

    // Weekly defaults: прошлая неделя
    const today  = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7) - 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    setStartDate(monday.toISOString().split('T')[0]);
    setEndDate(sunday.toISOString().split('T')[0]);

    // Monthly defaults: прошлый месяц
    const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    setSelectedYear(prev.getFullYear());
    setSelectedMonths([prev.getMonth() + 1]);
  }, []);

  const closePopup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setShowPopup(false);
    setScrapedRows([]);
    setScrapeStatus(null);
    setUploadStatus(null);
    setUploadErrors([]);
  }, []);

  const handleModeChange = useCallback((newMode) => {
    setMode(newMode);
    setScrapedRows([]);
    setScrapeStatus(null);
    setUploadStatus(null);
    setUploadErrors([]);
  }, []);

  // ── Хелперы выбора месяцев ─────────────────────────────────────────────
  const toggleMonth = useCallback((month) => {
    setSelectedMonths(prev =>
      prev.includes(month)
        ? prev.filter(m => m !== month)
        : [...prev, month].sort((a, b) => a - b)
    );
  }, []);

  const selectAllMonths  = useCallback(() => setSelectedMonths([1,2,3,4,5,6,7,8,9,10,11,12]), []);
  const clearAllMonths   = useCallback(() => setSelectedMonths([]), []);

  // ── SSE helper ─────────────────────────────────────────────────────────
  const connectSSE = useCallback((jobId) => {
    const es = new EventSource(`${API_URL}/api/sellerise/status/${jobId}`);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.error) {
        setScrapeStatus({ type: 'error', message: data.error, percent: 0 });
        es.close();
        return;
      }

      setScrapeStatus({
        type:    data.done ? 'success' : 'loading',
        message: data.step,
        percent: data.percent,
      });

      if (data.done && data.rows) {
        setScrapedRows(data.rows);
        es.close();
      }
    };

    es.onerror = () => {
      setScrapeStatus({ type: 'error', message: 'Connection lost. Check server.', percent: 0 });
      es.close();
    };
  }, []);

  // ── Fetch weekly ───────────────────────────────────────────────────────
  const handleFetchWeekly = useCallback(async () => {
    if (!startDate || !endDate) {
      setScrapeStatus({ type: 'error', message: 'Please select start and end dates', percent: 0 });
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setScrapeStatus({ type: 'error', message: 'Start date must be before end date', percent: 0 });
      return;
    }

    setScrapedRows([]);
    setUploadStatus(null);
    setUploadErrors([]);
    setScrapeStatus({ type: 'loading', message: 'Starting browser...', percent: 0 });

    try {
      const { data: { jobId } } = await api.post('/api/sellerise/scrape', { startDate, endDate });
      connectSSE(jobId);
    } catch (err) {
      const message = err.response ? (err.response.data?.error || 'Failed to start scraping') : err.message;
      setScrapeStatus({ type: 'error', message, percent: 0 });
    }
  }, [startDate, endDate, connectSSE]);

  // ── Fetch monthly (несколько месяцев) ──────────────────────────────────
  const handleFetchMonthly = useCallback(async () => {
    if (selectedMonths.length === 0) {
      setScrapeStatus({ type: 'error', message: 'Please select at least one month', percent: 0 });
      return;
    }

    setScrapedRows([]);
    setUploadStatus(null);
    setUploadErrors([]);
    setScrapeStatus({ type: 'loading', message: 'Starting browser...', percent: 0 });

    try {
      // Передаём массив выбранных месяцев
      const { data: { jobId } } = await api.post('/api/sellerise/scrape-month', { year: selectedYear, months: selectedMonths });
      connectSSE(jobId);
    } catch (err) {
      const message = err.response ? (err.response.data?.error || 'Failed to start monthly scraping') : err.message;
      setScrapeStatus({ type: 'error', message, percent: 0 });
    }
  }, [selectedYear, selectedMonths, connectSSE]);

  const handleFetch = useCallback(() => {
    if (mode === 'monthly') return handleFetchMonthly();
    return handleFetchWeekly();
  }, [mode, handleFetchWeekly, handleFetchMonthly]);

  // ── Upload ─────────────────────────────────────────────────────────────
  const handleUpload = useCallback(async () => {
    if (!scrapedRows.length) return;
    setUploadStatus({ type: 'loading', message: 'Saving to database...' });

    const endpoint = mode === 'monthly'
      ? '/api/sellerise/upload-month'
      : '/api/sellerise/upload';

    try {
      const { data } = await api.post(endpoint, { rows: scrapedRows });
      if (!data.success) throw new Error(data.error || 'Upload failed');

      setUploadStatus({ type: 'success', message: data.message });
      setUploadErrors(data.errorMessages || []);
      if (onSuccess) onSuccess();
    } catch (err) {
      const message = err.response ? (err.response.data?.error || 'Upload failed') : err.message;
      setUploadStatus({ type: 'error', message });
    }
  }, [scrapedRows, mode, onSuccess]);

  return {
    mode, handleModeChange,
    showPopup, openPopup, closePopup,
    startDate, setStartDate,
    endDate, setEndDate,
    selectedYear, setSelectedYear,
    selectedMonths, toggleMonth, selectAllMonths, clearAllMonths,
    monthNames: MONTH_NAMES,
    scrapedRows,
    scrapeStatus,
    uploadStatus,
    uploadErrors,
    handleFetch,
    handleUpload,
  };
}